import React, { FC, useEffect, useRef, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { Form, Input, InputRef, Popover, Select, Spin } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { ScopeSummaryStore, ScopeSummary } from 'src/stores/scopes-store';
import { Button } from 'antd';
import { OptionProps } from 'antd/lib/select';
import { ScopeType } from 'src/common/types';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const isSetsEqual = (a: Set<any>, b: Set<any>) => a.size === b.size && [...a].every((value) => b.has(value));

const ScopeHierarchySelector: FC<
  {
    onChange?: (value: {
      scopeId: string;
      scopeTitle: string;
      scopeType: ScopeType;
      parentId: string;
      path: string[];
      idx?: number;
      lastScope?: ScopeType;
    }) => void;
    type?: string;
    allowAdd?: boolean;
    partialSelectionMode?: boolean;
    allowedScopeRoots?: string[];
    maxScope?: ScopeType;
  } & WithTranslation
> = ({ t, onChange, type, allowAdd, allowedScopeRoots, partialSelectionMode, maxScope }) => {
  const availableScopeRoots = partialSelectionMode ? undefined : allowedScopeRoots;
  const unlockedScopeRoots = partialSelectionMode ? allowedScopeRoots : undefined;

  const scopesStore = container.get(ScopeSummaryStore);
  const [currentPath, setPath] = useState<string[]>([]);
  const scopePath = useRef<ScopeType[]>([]);
  const [loadedData, setData] = useState<any>({});
  const [currentLoading, setCurrentLoading] = useState(new Set<string>());
  const [allowedRoots, setAllowedRoots] = useState<string[]>([]);
  const newScopeNameInputRef = useRef<InputRef>(null);
  const newScopeTypeInputRef = useRef<InputRef>(null);
  const restarting = useRef<boolean>(false);
  const optionsTrack = useRef<any[]>([]);

  const load = (parentId: string) => {
    currentLoading.add(parentId);
    setCurrentLoading(new Set<string>(currentLoading.values()));

    let filter: any[] =
      type === undefined ? [`ParentId eq ${parentId}`] : [`ScopeType eq ${type ? `'${type}'` : 'null'} and ParentId eq ${parentId}`];

    if (parentId === 'null' && (availableScopeRoots || []).length !== 0) {
      filter = [...filter, `(id eq ${availableScopeRoots?.join(' or id eq ')})`];
    }

    scopesStore
      .getData({
        searchQuery: '',
        orderBy: [{ field: 'title', direction: 'Ascending', useProfile: false }],
        skip: 0,
        take: 1000000,
        filter,
      } as Query)
      .then((data: any) => {
        loadedData[parentId] = data;
        setData({ ...loadedData });
        currentLoading.delete(parentId);
        setCurrentLoading(new Set<string>(currentLoading.values()));
      });
  };

  const restart = (aer?: string[]) => {
    setAllowedRoots(aer || []);
    setData({ null: [] });
    setPath(['null']);
    scopePath.current = ['null' as any];
    setCurrentLoading(new Set());
    restarting.current = true;
    // onChange && onChange({ path: [] } as any);
    load('null');
  };

  useEffect(() => {
    restart([]);
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s1 = new Set(allowedRoots);
    const s2 = new Set(availableScopeRoots);
    if (!isSetsEqual(s1, s2)) {
      restart(availableScopeRoots);
    }
  }, [availableScopeRoots]); // eslint-disable-line react-hooks/exhaustive-deps

  const getData = (parentId: string): ScopeSummary[] => {
    return loadedData[parentId] || [];
  };

  const checkMaxScope = (a?: ScopeType, b?: ScopeType) => {
    if (!a || !b) return true;
    switch (a) {
      case ScopeType.Organization:
        return true;
      case ScopeType.Project:
        return !(b === ScopeType.Organization);
      case ScopeType.Ring:
        return !(b === ScopeType.Organization || b === ScopeType.Project);
      default:
        return false;
    }
  };

  const onSelect = (parentId: string, idx: number, value: string, option: any) => {
    const optionElem = option as OptionProps & { type: ScopeType };

    let nextPath = currentPath.slice(0, idx + 1);
    let nextScopePath = scopePath.current.slice(0, idx + 1);
    for (let i = idx + 1; i < currentPath.length; i++) loadedData[currentPath[i]] = [];
    const maxScopeCondition = !maxScope || checkMaxScope(optionElem?.type, maxScope);
    if (value && maxScopeCondition) {
      nextPath = [...nextPath, value];
      nextScopePath = [...nextScopePath, optionElem.type];
      if (!maxScope) setPath(nextPath);
      scopePath.current = nextScopePath;
      setData({ ...loadedData });
      load(value);

      const newOptionsTrack = new Array(scopePath.current.length - 1);
      if (newOptionsTrack.length > optionsTrack.current.length) {
        optionsTrack.current.push(optionElem);
      } else {
        for (let i = 0; i < newOptionsTrack.length; i++) {
          newOptionsTrack[i] = optionsTrack.current[i];
        }
        newOptionsTrack[newOptionsTrack.length - 1] = optionElem;
        optionsTrack.current = newOptionsTrack;
      }

      onChange &&
        onChange({
          scopeId: value === 'null' ? (null as any) : value,
          scopeTitle: optionElem.title as string,
          scopeType: optionElem.type,
          parentId: (parentId === 'null' ? null : parentId) as string,
          path: nextPath,
          idx: idx,
          lastScope: scopePath.current.at(-1),
        });
    } else {
      const lastParentId = nextPath.slice(-1)[0];
      setPath(nextPath);
      scopePath.current = nextScopePath;

      const newOptionsTrack = new Array(scopePath.current.length - 1);

      for (let i = 0; i < newOptionsTrack.length; i++) {
        newOptionsTrack[i] = optionsTrack.current[i];
      }
      optionsTrack.current = newOptionsTrack;
      const currentInfo = newOptionsTrack[newOptionsTrack.length - 1];

      setData({ ...loadedData });
      onChange &&
        onChange({
          scopeId: lastParentId === 'null' ? (null as any) : lastParentId,
          scopeTitle: currentInfo?.title as string,
          scopeType: currentInfo?.scopeType as ScopeType,
          parentId: (currentInfo?.parentId === 'null' ? null : currentInfo?.parentId) as string,
          path: nextPath,
          idx: idx,
          lastScope: scopePath.current.at(-1),
        });
    }
  };

  const getSelectedValue = (parentId: string, idx: number) => {
    let val = undefined;
    const value = currentPath[idx + 1];
    if (value) {
      const data = getData(parentId);
      const currentInfo = data.find((x: ScopeSummary) => x.id === value);
      val = { value: value, label: currentInfo?.title };
    }
    if (restarting.current) {
      restarting.current = false;
      return null;
    }
    return val?.value ?? undefined;
  };

  const onAddData = (parentId: string, idx: number, newScopeTitle: string, newScopeType: ScopeType) => {
    if (newScopeTitle == null || newScopeTitle.length === 0) return;
    if (newScopeType == null || newScopeType.length === 0) return;

    const data: ScopeSummary[] = getData(parentId);
    if (data.length !== 0 && data.slice(-1)[0].id === EMPTY_GUID) {
      data.slice(-1)[0].title = newScopeTitle;
    } else {
      data.push({
        id: EMPTY_GUID,
        code: -1,
        title: newScopeTitle,
        scopeType: newScopeType,
        hierarchy: '',
        parentId: parentId,
      });
    }
    setData({ ...loadedData });
    onSelect(parentId, idx, EMPTY_GUID, { title: newScopeTitle, id: EMPTY_GUID, type: newScopeType });
  };

  return (
    <div className="entity-hierarchy-selector-flex-container" style={{ display: 'flex', alignItems: 'center', flexFlow: 'wrap' }}>
      {currentPath
        .filter((x) => x !== EMPTY_GUID)
        .flatMap((parentId, idx) =>
          parentId === 'null' || unlockedScopeRoots === undefined || unlockedScopeRoots?.includes(parentId) || idx > 1
            ? [
                <React.Fragment key={parentId}>
                  {idx !== 0 && (
                    <span className="hierarchy-slash" style={{ margin: '5px 5px' }}>
                      /
                    </span>
                  )}
                  <Select
                    onClear={() => {}}
                    dropdownMatchSelectWidth={false}
                    loading={currentLoading.has(parentId)}
                    placeholder={t('SelectEntity')}
                    onChange={(value, option) => onSelect(parentId, idx, (value as unknown) as string, option)}
                    style={{ width: '35%', margin: '5px 0' }}
                    allowClear
                    showSearch
                    value={getSelectedValue(parentId, idx)}
                    notFoundContent={currentLoading.has(parentId) ? <Spin size="small" /> : undefined}
                    filterOption={(input, option) => (option as any)?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  >
                    {getData(parentId).map((v: ScopeSummary) => (
                      <Select.Option key={v.id} value={v.id} type={v.scopeType} title={v.title}>
                        {v.title}
                      </Select.Option>
                    ))}
                  </Select>
                  {allowAdd && idx + 1 === currentPath.length && currentPath[idx] !== EMPTY_GUID && (
                    <Popover
                      placement="bottom"
                      title={t('Add new Scope')}
                      content={
                        <Form>
                          <Form.Item>
                            <Input ref={newScopeNameInputRef} placeholder={t('Set Scope Title')} />
                          </Form.Item>

                          <Form.Item>
                            <Input ref={newScopeTypeInputRef} placeholder={t('Set Scope Type')} />
                          </Form.Item>

                          <Form.Item noStyle>
                            <Button
                              type="primary"
                              // style={{ marginBottom: 0 }}
                              onClick={() =>
                                onAddData(
                                  parentId,
                                  idx,
                                  newScopeNameInputRef.current?.input?.value || '',
                                  (newScopeTypeInputRef.current?.input?.value as ScopeType) || ''
                                )
                              }
                            >
                              {t('Add')}
                            </Button>
                          </Form.Item>
                        </Form>
                      }
                      trigger="click"
                    >
                      <Button style={{ margin: '5px 5px' }}> {t('Add New')}</Button>
                    </Popover>
                  )}
                </React.Fragment>,
              ]
            : []
        )}
    </div>
  );
};

export default withTranslation()(ScopeHierarchySelector);
